import React, {useContext, useEffect, useState} from "react";
import {AppContext} from "../../context/AppContext";
import {registerWorkInitialState} from "./registerWorkInitialState";
import {Button, Stack, Typography, CircularProgress} from "@mui/material";
import {ChooseTemplate} from "./components/ChooseTemplate";
import {ProjectInformation} from "./components/ProjectInformation";
import {NftInformation} from "./components/NftInformation";
import {auth, db, storage} from "../../config/firebase";
import {toast} from "react-toastify";
import {getDownloadURL, ref, uploadBytes} from "firebase/storage";
import {doc, getDoc, setDoc, updateDoc} from "firebase/firestore";
import {StripeEmbeddedForm} from "../../components/stripe/StripeEmbeddedForm";
import {grey} from "@mui/material/colors";
import {IconChevronLeft} from "@tabler/icons-react";
import {useNavigate, useParams} from "react-router-dom";
import useWalletConfirm from "../../hooks/useWalletConfirm";
import {NftCreated} from "./components/NftCreated";
import MintingLoadingScreen from "../../components/lottie-animations/MintingLoadingScreen";
import {SubmittingWorkProgress} from "../../utils/enums/submitting-work-progress";
import {PhantomWalletPrompt} from "../../components/modals/PhantomWalletPrompt";
import Box from "@mui/material/Box";
import {StripeProductTypes} from "../../utils/enums/StripeProductTypes";
import axios from "axios";

export const RegisterWorkNew = () => {
  const {state: {royaltyTemplates, userDetails}, dispatch} = useContext(AppContext);
  const [step, setStep] = useState(0);
  const [registerWorkForm, setRegisterWorkForm] = useState({...registerWorkInitialState})
  const [publicWalletAddress, setPublicWalletAddress] = useState(undefined);
  const [allChecksPassed, setAllChecksPassed] = useState(false)
  const [status, setStatus] = useState('');
  const [submittingStatus, setSubmittingStatus] = useState('');
  const [doneStatuses, setDoneStatuses] = useState([]);
  const [workSaved, setWorkSaved] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isMinting, setIsMinting] = useState(false);
  const [mintingError, setMintingError] = useState(null);
  const [nftTxnHash, setNftTxnHash] = useState('');
  const {nftId} = useParams();
  const navigate = useNavigate();
  const [WalletDialog, confirmDialog] = useWalletConfirm("Are you sure you want to save your work for later?", "If you don’t connect your wallet, we will still mint your work as an NFT. When you have a wallet, you can click “Transfer” to move your NFT from our wallet to yours.")

  useEffect(() => {
    const fetchNft = async () => {
      const docRef = doc(db, "royalty_templates", nftId);
      const docSnap = await getDoc(docRef);

      if (docSnap?.exists()) {
        const nftData = {...docSnap?.data()}
        setRegisterWorkForm({...nftData})
        setPublicWalletAddress(nftData?.publicWalletAddress || undefined);
        if (nftData?.publicWalletAddress) {
          setAllChecksPassed(true);
          setStep(3);
        } else {
          setStep(2);
        }
      }
    }
    if (nftId) {
      fetchNft();
    }
  }, [nftId])

  useEffect(() => {
    window.scrollTo({behavior: "smooth", top: 0});
  }, [step])

  useEffect(() => {
    const newState = {};
    if (userDetails?.projects?.length) {
      newState.projectTypes = [userDetails?.projects[0]] || [];
    }

    setRegisterWorkForm(prev => ({...prev, ...newState}))
  }, [userDetails])

  const onUpdate = (key, value) => {
    setRegisterWorkForm(prevState => {
      return {...prevState, [key]: value}
    })
  }

  const onSubmit = async () => {
    let step = 1;
    if (!publicWalletAddress && !registerWorkForm?.publicWalletAddress) {
      const response = await confirmDialog();
      if (!response) {
        return;
      }
      step = 2;
    }
    const data = {...registerWorkForm};
    if (nftId) {
      data.uid = nftId;
    }
    data.createdAt = new Date().getTime()
    data.userUid = auth.currentUser.uid
    data.paid = false
    data.nftTxnHash = ''
    data.publicWalletAddress = registerWorkForm?.publicWalletAddress || publicWalletAddress || null;

    const file = registerWorkForm?.file;
    const publicImage = registerWorkForm?.publicImage;
    //TODO: check if all of the mandatory fields have been filled out
    //  change state to reflect that to render payment and save the work
    if (!file) {
      toast.error('No file, please upload a file and try again!')
      return;
    }

    setAllChecksPassed(true)
    toast.info("Saving your work...")
    setSubmitLoading(true)
    try {
      if (typeof file !== "string") {
        const fileFolderRef = ref(storage, `royalty_templates/${file?.name}`)
        console.log("Uploading image")
        setSubmittingStatus(SubmittingWorkProgress.UPLOAD_WORK)
        const imgRef = await uploadBytes(fileFolderRef, file);
        data.file = await getDownloadURL(imgRef.ref);
        setDoneStatuses([SubmittingWorkProgress.UPLOAD_WORK]);
      }
      if (typeof publicImage !== "string") {
        setSubmittingStatus(SubmittingWorkProgress.UPLOAD_PUBLIC_IMAGE)
        const fileFolderRef = ref(storage, `royalty_templates/${publicImage?.name}`)
        const publicImgRef = await uploadBytes(fileFolderRef, publicImage);
        data.publicImage = await getDownloadURL(publicImgRef.ref);
        setDoneStatuses(prev => ([...prev, SubmittingWorkProgress.UPLOAD_PUBLIC_IMAGE]));
      }

      setSubmittingStatus(SubmittingWorkProgress.SAVE_WORK);
      await setDoc(doc(db, "royalty_templates", data.uid), data);
      setDoneStatuses(prev => ([...prev, SubmittingWorkProgress.SAVE_WORK]));
      const templates = [...royaltyTemplates];
      const index = templates?.findIndex(template => template?.uid === data?.uid);
      if (index >= 0) {
        templates[index] = data;
      } else {
        templates.unshift(data);
      }
      dispatch({type: "update_royalty_templates", payload: templates});
      setWorkSaved(true);
      setSubmittingStatus(SubmittingWorkProgress.PAYMENT);
      setDoneStatuses(prev => ([...prev, SubmittingWorkProgress.PAYMENT]));
      if (step === 2) {
        toast.success("Work saved");
        navigate("/portfolio")
      } else {
        setStep(prev => prev + step);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Save failed, please try again!')
      setSubmittingStatus(SubmittingWorkProgress.FAILED);
    } finally {
      setSubmitLoading(false);
      setSubmittingStatus("");
      setDoneStatuses([])
    }
  }


  const handleMinting = async () => {
    // Prevent minting if already in progress or if essential data is missing
    if (isMinting || !registerWorkForm.uid) {
      console.error("Minting prerequisites not met:", { isMinting, uid: registerWorkForm.uid, publicWalletAddress });
      toast.error("Cannot proceed with minting. Required information is missing.");
      return;
    }

    setIsMinting(true);
    setMintingError(null);
    setSubmittingStatus(SubmittingWorkProgress.MINTING);
    setDoneStatuses(prev => ([...prev, SubmittingWorkProgress.MINTING]));

    const mintUrl = process.env.REACT_APP_ENVIRONMENT === 'development' ?
      'http://localhost:5001/c3-nft-c3/us-central1/mintNft' :
      'https://us-central1-c3-nft-c3.cloudfunctions.net/mintNft';

    console.log(userDetails)
    const params = {
      nftUid: registerWorkForm.uid,
      publicAddress: publicWalletAddress,
      productType: StripeProductTypes.NFT,
      userEmail: userDetails.email
    };

    try {
      console.log("Calling mintNft endpoint with params:", params);
      const response = await axios.get(mintUrl, { params });
      console.log("Minting response:", response.data);

      if (response.data && response.data.transaction_hash) {
        // Minting successful, update Firestore with txn hash
        const nftDocRef = doc(db, "royalty_templates", registerWorkForm.uid);
        await updateDoc(nftDocRef, {
          nftTxnHash: response.data.transaction_hash,
          paid: true
        });

        // Update local state
        setNftTxnHash(response.data.transaction_hash);
        setRegisterWorkForm(prev => ({ ...prev, nftTxnHash: response.data.transaction_hash, paid: true }));

        // Update templates in context
        const templates = [...royaltyTemplates];
        const index = templates?.findIndex(template => template?.uid === registerWorkForm?.uid);
        if (index >= 0) {
          templates[index] = { ...templates[index], nftTxnHash: response.data.transaction_hash, paid: true };
          dispatch({type: "update_royalty_templates", payload: templates});
        }

        toast.success("NFT Minted Successfully!");
        setSubmittingStatus(SubmittingWorkProgress.MINTING_COMPLETE);
        setDoneStatuses(prev => ([...prev, SubmittingWorkProgress.MINTING_COMPLETE]));
        setStep(4); // Move to the final "NFT Created" step
      } else {
        // Handle cases where backend indicates failure even with 200 status
        const errorMsg = response.data?.message || 'Minting failed. Backend response indicates an issue.';
        console.error("Minting failed:", errorMsg);
        setMintingError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error calling mintNft endpoint:', error);
      const errorMsg = error.response?.data || error.message || "An unexpected error occurred during minting.";
      setMintingError(errorMsg);
      toast.error(`Minting failed: ${errorMsg}`);
    } finally {
      setIsMinting(false);
      // Reset submitting status after a delay to show the completion message
      setTimeout(() => {
        setSubmittingStatus("");
        setDoneStatuses([]);
      }, 2000);
    }
  };

  // Handler for payment success or free user bypass
  const handlePaymentSuccessOrBypass = async (details) => {
    console.log("Payment/Bypass successful, proceeding to minting. Details:", details);
     await handleMinting();
  };

  const onNextStep = () => setStep(prev => prev + 1);
  const onPrevStep = () => setStep(prev => prev - 1);

  return (
    <Stack id={"form-wrapper"} direction={"column"} gap={5} maxWidth={"lg"} sx={{py: 5, px: 2, width: "90%", mx: "auto", textAlign: "left !important", overflowX: "hidden"}}>
      <WalletDialog />
      <PhantomWalletPrompt />
      <Box sx={{display: step === 4 ? "none" : "block"}}>
        <Typography variant={"h2"} fontWeight={700} fullWidth align={"center"} gutterBottom>Document a New Creative Work</Typography>
        <Typography className={"text-grey"} fontWeight={400} fullWidth align={"center"}>On blockchain to protect your work with a common law copyright.</Typography>
        {/* <Typography
            style={{ color: "#717171", fontStyle: "italic", marginTop: "20px" }}
            fontWeight={400}
            fullWidth
            align="center"
        >
          The C3 Team currently covers all minting costs for subscribers.
          <br />
          To mint, select <b>"Pay with Card"</b> and enter <b>4242 4242 4242 4242</b> with <b>any name, CVC, country, and expiration date</b> in the future.
        </Typography> */}
 </Box>
      {
        submitLoading ? <MintingLoadingScreen message={submittingStatus} progress={doneStatuses} /> : ({
          0: <ChooseTemplate onNextStep={onNextStep} />,
          1: <ProjectInformation onNextStep={onNextStep} onPrevStep={onPrevStep} onUpdate={onUpdate} registerWorkForm={registerWorkForm} />,
          2: <NftInformation
                onNextStep={onNextStep} onPrevStep={onPrevStep}
                onSubmit={onSubmit} onUpdate={onUpdate}
                workSaved={workSaved} loading={submitLoading} registerWorkForm={registerWorkForm}
                publicWalletAddress={publicWalletAddress} setPublicWalletAddress={setPublicWalletAddress}
          />,
          3: <>
            <StripeEmbeddedForm
              nftUid={nftId || registerWorkForm.uid}
              publicAddress={publicWalletAddress}
              allChecksPassed={allChecksPassed}
              setStatus={setStatus}
              productType={StripeProductTypes.NFT}
              onNextStep={handlePaymentSuccessOrBypass}
            />

            {/* Show Minting Loader/Error during minting */}
            {isMinting && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Minting your NFT...</Typography>
              </Box>
            )}
            {mintingError && (
              <Typography color="error" sx={{ textAlign: 'center', p: 2 }}>
                Minting Error: {mintingError}
              </Typography>
            )}

            <Stack direction={"row"} gap={2}>
              <Button
                disabled={step === 0 || isMinting}
                sx={{
                  textTransform: "none",
                  border: 0,
                  color: `${grey[600]} !important`
                }}
                onClick={() => setStep(step - 1)}
              >
                <IconChevronLeft size={18}/>
                Back
              </Button>
            </Stack>
          </>,
          4: <NftCreated nft={registerWorkForm} />
        }[step])
      }
    </Stack>
  )
}
